import type {
  ListRootFiCompaniesResponseV4,
  RootfiIntegrationListItem,
} from '@commenda/types';
import type { TransferPricingStatementSection } from '@prisma/client';
import type { KeyedMutator } from 'swr';
import type { TTransferPricingAccountPeriodBalance } from '../transfer-pricing/policy/types';

export { RootfiCompanyUseCase } from '@prisma/client';

export enum RootfiAccountCategory {
  ASSET = 'ASSET',
  EXPENSE = 'EXPENSE',
  LIABILITY = 'LIABILITY',
  EQUITY = 'EQUITY',
  INCOME = 'INCOME',
  UNKNOWN = 'UNKNOWN',
}

export interface RootfiAccount {
  rootfi_id: number;
  rootfi_created_at: string;
  rootfi_updated_at: string;
  rootfi_deleted_at: string | null;
  rootfi_company_id: number;
  platform_id: string;
  platform_unique_id: string;
  currency_id: string;
  parent_account_id: string;
  custom_fields: string | null;
  nominal_code: string | null;
  name: string;
  description: string | null;
  current_balance: number;
  updated_at: string;
  status: string;
  category: RootfiAccountCategory;
  sub_category: string;
  credit_amount: number;
  debit_amount: number;
  currency: string;
}

export interface RootfiAccountMapping {
  id: string;
  transferPricingPolicyId: string | null,
  serviceProviderCorporationId: number,
  glAccountId: string,
  glAccountCategory: RootfiAccountCategory,
  glAccountSubCategory: string,
  glAccountName: string,
  glAccountCurrency: string,
  /** The ERP's own account number; null until the next sync reads it, or when the ERP has none. */
  glAccountNumber: string | null,
  /** The income-statement section the account sits in; null until the next sync reads it. */
  glStatementSection: TransferPricingStatementSection | null,
  profitAndLossCategory: string | null,
  allocationMethod: string | null,
  allocationPercentage: number | null,
  isEnabled: boolean | null,
  balances: TTransferPricingAccountPeriodBalance[],
  /** Sum of `balances` — the account's ledger movement across the window the mappings were read for. */
  periodBalance: number,
  createdAt: string,
  updatedAt: string,
}

export interface RootfiReportSection {
  name: string;
  value: number;
  line_items: RootfiReportSection[] | null;
}

export interface RootfiIncomeStatement {
  period_start?: string;
  period_end?: string;
  revenue: RootfiReportSection[];
  cost_of_goods_sold: RootfiReportSection[];
  gross_profit: number | null;
  operating_expenses: RootfiReportSection[];
  operating_profit: number | null;
  non_operating_revenue: RootfiReportSection[];
  non_operating_expenses: RootfiReportSection[];
  earnings_before_taxes: number | null;
  net_profit: number | null;
}

export interface RootfiCashFlowStatement {
  period_start?: string;
  period_end?: string;
  operating_activities: RootfiReportSection[] | null;
  investing_activities: RootfiReportSection[] | null;
  financing_activities: RootfiReportSection[] | null;
  cash_at_beginning_of_period: number | null;
  cash_at_end_of_period: number | null;
}

export interface RootfiBalanceSheet {
  period_start?: string;
  period_end?: string;
  net_assets: number | null;
  assets: RootfiReportSection[];
  liabilities: RootfiReportSection[];
  equity: RootfiReportSection[];
}

type RootfiSyncActionStatus = "cancelled" | "retry_initiated" | "already_completed";

export interface RootfiSyncActionResponse {
  sync_id: string;
  status: RootfiSyncActionStatus;
  jobs_affected?: number;
  message: string;
}

export interface StatementFilters {
  periodStart?: string;
  periodEnd?: string;
  period?: number;
}

export type UseRootFiIntegrationsResult = {
  data: RootfiIntegrationListItem[];
  error: unknown;
  isLoading: boolean;
  mutate: KeyedMutator<ListRootFiCompaniesResponseV4>;
};

export type UseRootFiCompanyResult = {
  data: RootfiIntegrationListItem | undefined;
  error: unknown;
  isLoading: boolean;
  mutate: KeyedMutator<RootfiIntegrationListItem>;
};
